import React from 'react';
import Slider from 'components/commercetools-ui/atoms/slider';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Image from 'frontastic/lib/image';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';
import { Tiempos, NationalBook } from 'fonts';
import { GenericSectionContent, MediaCard, Cta } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import { sanitizeHtml, SECTION_SPACING } from '../../../helpers/rendering';

/**
 * Lyra — the image-and-copy panel: image on one side, a grey panel with heading, rich text and CTAs
 * on the other. Port of the legacy `homepage/image-content-slider` ("Visit us at the Methven
 * Experience Centre").
 *
 * Two things it deliberately does NOT do, both of which it used to:
 *
 *   1. It is not a carousel for a single card. Wrapping one slide in a Slider still mounts Swiper
 *      and still paints arrows, so the section offered navigation between one panel and itself.
 *      The carousel appears only once there is something to page through.
 *   2. It does not impose a height. The old panel padding (`pb-100 lg:pb-140 2xl:pb-300`) set the
 *      row height, and the image was stretched to fill it with `objectFit: cover` -- so the section
 *      was a fixed 620px tall whatever image the entry carried, and the image was cropped to suit.
 *      The image now renders at its own aspect ratio and the row is as tall as the image is, which
 *      is what the legacy component did by rendering a plain <img> with no height.
 */
export default function LyraRendering({ content }: { content: GenericSectionContent }) {
  const { cards = [], additional_css, additional_css_class_names } = content;
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'LyraRendering');
  const [isDesktopSize] = useMediaQuery(desktop);
  const isCarousel = cards.length > 1;

  const panel = (card: MediaCard, index: number) => (
    <div key={index} className="flex flex-col bg-[#EDEDED] lg:flex-row">
      {/*
        Natural aspect: the image sets the row's height instead of being cropped into it.

        The column is sized the way the legacy panel sized it -- the image contributes its own width
        (capped at the 1300px it was authored for) and the copy beside it takes what is left, with
        both free to shrink. A fixed 60% split looks close but reads ~7% wider than the live page.
      */}
      <div className="relative w-full lg:w-[56%]">
        {card?.desktop_image?.url && (
          <Image
            loading="lazy"
            src={card.desktop_image.url}
            alt={card.desktop_image.description ?? card?.title ?? ''}
            sizes="(max-width: 1024px) 100vw, 60vw"
            width={1300}
            height={1073}
            //A DEFINITE column width (56%) rather than a content-sized one. Content sizing matched
            //the legacy split to within 8px once loaded, but the column had nothing to size itself by
            //until the lazily-loaded image arrived, so the panel opened narrow and jumped. 56% is
            //what the legacy flex split settles at on a 1920 viewport (1049 of 1880). The
            //width/height pair below reserves the height, so nothing moves as the image loads.
            style={{ width: '100%', height: 'auto' }}
            className="brightness-75"
          />
        )}
      </div>
      <div className="flex-1 px-24 py-24 pb-50 lg:px-48 lg:pb-50 lg:pt-48 xl:pb-100 2xl:pb-232">
        {card?.title && (
          <div className="md:w-[90%] xl:w-[65%] 2xl:w-[55%]">
            <Typography as="h2" className={`text-left text-24 font-medium leading-8 text-[#090909] xl:text-36 xl:leading-[3.25rem] 2xl:text-48 ${Tiempos.className}`}>
              {card.title}
            </Typography>
          </div>
        )}
        {card?.description && (
          <div className="xl:w-[78%] 2xl:w-[65%]">
            <div
              className={`mt-16 text-left text-16 font-normal leading-6 text-[#333] lg:mt-22 lg:leading-8 2xl:text-20 ${NationalBook.className}`}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.description) }}
            />
          </div>
        )}
        {card?.ctas && card.ctas.length > 0 && (
          <div className="mt-24 flex flex-col gap-10 lg:mt-48 lg:flex-row">
            {card.ctas.map((cta: Cta, i: number) => (
              <Link key={i} link={cta.url} target={cta.target ?? '_self'} rel="noopener noreferrer">
                <Button className="border-1 w-full rounded-none border border-[#2B2C39] bg-[#2B2C39] px-32 py-18 shadow-sm hover:!bg-[#394A54] lg:w-fit">
                  <Typography as="span" className={`text-16 text-white xl:text-14 2xl:text-16 ${NationalBook.className}`}>
                    {cta.label}
                  </Typography>
                </Button>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`${SECTION_SPACING} image-content w-full px-20 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        {isCarousel ? (
          <Slider
            slidesPerView={1}
            dots={!isDesktopSize}
            arrows={isDesktopSize}
            innerArrows
            allowArrowsOnTouchDevice
            allowTouchMove
            prevButtonStyles={{ top: '90%', left: '88%', padding: '20px' }}
            nextButtonStyles={{ top: '90%', left: '94%', padding: '20px' }}
            className="bg-[#EDEDED]"
          >
            {cards.map(panel)}
          </Slider>
        ) : (
          cards.map(panel)
        )}
      </div>
    </>
  );
}
